import { ResponsiveLine } from '@nivo/line';
import { area, curveCardinal } from 'd3-shape';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatLargeNumber } from '../../utils/utils';
import { useTheme } from '@mui/material/styles';

// Map a sentiment string to a dot color for the event overlay.
export const sentimentDotColor = (theme, s) => (
    s === 'positive' ? theme.palette.success.main
        : s === 'negative' ? theme.palette.error.main
            : theme.palette.text.secondary
);

/**
 * Build the {x-bin-index} positions for workspace event markers that fall
 * within the (already windowed) bins' time span. Markers carry epoch-ms
 * ``t`` (occurred_at); each is dropped onto the bin whose [from,to) contains it.
 */
export const placeEventMarkers = (eventMarkers, windowed) => {
    const ms = Array.isArray(eventMarkers) ? eventMarkers : [];
    if (!ms.length || !Array.isArray(windowed) || windowed.length === 0) return [];
    const ranges = windowed
        .map((b, i) => {
            const f = b?.from ? Date.parse(b.from) : NaN;
            const t = b?.to ? Date.parse(b.to) : f;
            return { i, f, t };
        })
        .filter((r) => Number.isFinite(r.f));
    if (!ranges.length) return [];
    const lo = ranges[0].f;
    const hi = ranges[ranges.length - 1].t || ranges[ranges.length - 1].f;
    const placed = [];
    ms.forEach((m) => {
        const tt = Number(m?.t);
        if (!Number.isFinite(tt) || tt < lo || tt > hi) return;
        let idx = ranges.findIndex((r) => tt >= r.f && tt < (r.t || r.f + 1));
        if (idx < 0) idx = ranges.length - 1;
        placed.push({
            x: ranges[idx].i,
            sentiment: m.sentiment,
            rank: m.rank,
            stream: m.stream,
            t: tt,
        });
    });
    return placed;
};

const useMousePosition = () => {
    const [
        mousePosition,
        setMousePosition
    ] = React.useState({ x: null, y: null });
    
    React.useEffect(() => {
        const updateMousePosition = ev => {
            setMousePosition({ x: ev.clientX, y: ev.clientY });
        };
        
        const resetMousePosition = () => {
            setMousePosition({ x: null, y: null });
        };
        
        window.addEventListener('mousemove', updateMousePosition);
        window.addEventListener('mouseleave', resetMousePosition);
        
        return () => {
            window.removeEventListener('mousemove', updateMousePosition);
            window.removeEventListener('mouseleave', resetMousePosition);
            // Reset position on cleanup
            setMousePosition({ x: null, y: null });
        };
    }, []);
    
    return mousePosition;
};



const CustomTooltip = ({ point, unit }) => {
    const bin = point.data.bin;
    const mousePosition = useMousePosition();
    
    // Don't render tooltip if mouse position is not available
    if (!mousePosition.x || !mousePosition.y) {
        return null;
    }
    
    let horizontalTransform = (mousePosition.x + 160) > window.innerWidth / 2 ? 'translateX(-100%)' : '';
    let verticalTransform = (mousePosition.y + 20) > window.innerHeight / 2 ? 'translateY(-100%)' : '';

    const tooltipStyle = {
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 9999,
        // backgroundColor: 'white',
        padding: '10px',
        transform: `${horizontalTransform} ${verticalTransform}`.trim(),
        left: mousePosition.x,
        top: mousePosition.y
    }

    const truncateString = (str, maxLength) => {
        if (typeof str !== 'string') return str;
        if (str.length > maxLength) {
            return str.substring(0, maxLength) + '...';
        }
        return str;
    };

    // ------------------------------------------------------------------
    // Field renderers
    //
    // The backend doesn't always populate every bin field - rolled-up
    // bins typically lack ``medianValue`` / ``p10`` / ``p90``, and the
    // anomaly detector returns ``{lower_bound: 0, upper_bound: 0,
    // anomalies: []}`` placeholders when there's nothing to flag.
    //
    // ``hasValue`` and ``Row`` collapse the noise: a row simply
    // doesn't render unless the value is genuinely available.
    // ------------------------------------------------------------------

    const hasValue = (v) =>
        v !== null
        && v !== undefined
        && v !== ''
        && !(typeof v === 'number' && Number.isNaN(v));

    const fmtNumber = (v) => {
        if (!hasValue(v) || typeof v !== 'number' || !Number.isFinite(v)) return null;
        if (Math.abs(v) >= 1000) return formatLargeNumber(v) || v.toLocaleString();
        return v.toLocaleString(undefined, {
            maximumFractionDigits: 4,
            minimumFractionDigits: 0,
        });
    };

    // Value rows carry the signal's measurement unit ("$/MWh"); counts
    // and dates never do.
    const fmtValue = (v) => {
        const s = fmtNumber(v);
        if (s === null || !unit) return s;
        return unit === '%' ? `${s}%` : `${s} ${unit}`;
    };

    const fmtDate = (v) => {
        if (!hasValue(v)) return null;
        try {
            const d = new Date(v);
            if (Number.isNaN(d.getTime())) return String(v);
            return d.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch (_e) {
            return String(v);
        }
    };

    const Row = ({ label, value }) => {
        if (value === null || value === undefined || value === '') return null;
        return (
            <div>
                <span>{label}</span>
                <span>{value}</span>
            </div>
        );
    };

    // Anomaly bounds: the wrapped anomaly objects ship
    // {lower_bound, upper_bound, anomalies:[]}. We only render bounds
    // when they're actually computed (both non-null AND not both zero,
    // which is the placeholder shape the backend uses for "no data").
    const renderAnomalies = (anomalies) => {
        if (!Array.isArray(anomalies) || anomalies.length === 0) return null;
        return anomalies.map((anomaly, index) => {
            const lb = anomaly?.lower_bound;
            const ub = anomaly?.upper_bound;
            const boundsComputed = (
                hasValue(lb) && hasValue(ub)
                && !(Number(lb) === 0 && Number(ub) === 0)
            );
            const points = Array.isArray(anomaly?.anomalies) ? anomaly.anomalies : [];
            const hasPoints = points.length > 0;

            // Skip the entire wrapper if neither bounds nor points are useful.
            if (!boundsComputed && !hasPoints) return null;

            return (
                <React.Fragment key={index}>
                    {boundsComputed && (
                        <>
                            <Row label="Lower bound (10%)" value={fmtNumber(Number(lb))} />
                            <Row label="Upper bound (90%)" value={fmtNumber(Number(ub))} />
                        </>
                    )}
                    {hasPoints && (
                        <Row
                            label="Anomalies"
                            value={truncateString(
                                Array.isArray(points[0]) ? points[0].join(', ') : String(points[0]),
                                200,
                            )}
                        />
                    )}
                </React.Fragment>
            );
        });
    };

    return createPortal(
        <div style={tooltipStyle}>
            <div className='mini-chart-tooltip'>
                <Row label="Bin" value={point.data.x + 1} />
                <Row label="Start" value={fmtDate(bin?.from)} />
                <Row label="End" value={fmtDate(bin?.to)} />
                <Row label="Data points" value={fmtNumber(Number(bin?.count))} />
                <Row label="First" value={fmtValue(Number(bin?.firstValue))} />
                <Row label="Last" value={fmtValue(Number(bin?.lastValue))} />
                <Row label="Min" value={fmtValue(Number(bin?.minValue))} />
                <Row label="Max" value={fmtValue(Number(bin?.maxValue))} />
                <Row label="Average" value={fmtValue(Number(bin?.avgValue))} />
                <Row label="StdDev" value={fmtValue(Number(bin?.stdDev))} />
                <Row label="Median" value={fmtValue(Number(bin?.medianValue))} />
                {renderAnomalies(bin?.anomalies)}
            </div>
        </div>,
        document.body,
    );
};

const CustomAreaLayer = ({ series, xScale, yScale }) => {
    // Assuming minValue and maxValue are the first two series
    const minValueData = series.find(s => s.id === 'minValue').data;
    const maxValueData = series.find(s => s.id === 'maxValue').data;

    const areaGenerator = area()
        .x(d => xScale(d.data.x))
        .y0(d => yScale(d.data.y))
        .y1((d, i) => yScale(maxValueData[i].data.y))
        .curve(curveCardinal); // Apply the cardinal curve to the area

    return (
        <path
            d={areaGenerator(minValueData)}
            fill="url(#gradient)"
            fillOpacity={0.7}
        />
    );
};

// NOTE: an in-chart volume strip used to live here as
// ``buildCustomVolumeLayer`` plus its ``VOLUME_*`` constants. It was
// retired when the per-signal ``SignalCard`` started rendering
// ``DataDensityStrip`` directly below this chart - keeping a second
// volume visualization inside the chart duplicated the information
// and made the chart look like it had bars baked in. If you ever
// want stock-chart-style price+volume back, restore it from git;
// the layer plugged into the ``customLayers`` array at index 5.

// Cap how many time-buckets we draw. We keep the most RECENT
// ``MAX_CHART_POINTS`` so the chart stays readable (and counts back by
// cadence implicitly, since bins are time-ordered oldest->newest).
export const MAX_CHART_POINTS = 100;

const SignalBinMiniChart = ({ data, eventMarkers, height = 63, maxPoints = MAX_CHART_POINTS, unit = null }) => {
    const theme = useTheme();
    const mouse = useMousePosition();
    const [hoveredMarker, setHoveredMarker] = useState(null);

    // Stable tooltip component. The chart re-renders on every mousemove
    // (useMousePosition above), so an inline arrow here would be a NEW
    // component type each render — React would unmount/remount the
    // tooltip continuously and it would never render (its own
    // useMousePosition starts at null on every remount).
    const BinTooltip = useMemo(
        () => (props) => <CustomTooltip {...props} unit={unit} />,
        [unit],
    );

    // Most-recent window: keep the last ``maxPoints`` bins (the detail view
    // passes Infinity to show the full history inside a scroll container).
    const windowed = Array.isArray(data) && Number.isFinite(maxPoints) && data.length > maxPoints
        ? data.slice(-maxPoints)
        : (data || []);

    // Event-occurrence dots overlaid on the value line (one per event that
    // falls within this signal's visible time span). Dots are interactive:
    // hovering one shows the event's stream / sentiment / rank / time.
    const placedMarkers = useMemo(
        () => placeEventMarkers(eventMarkers, windowed),
        [eventMarkers, windowed],
    );
    const EventMarkersLayer = useMemo(() => {
        if (!placedMarkers.length) return null;
        return ({ xScale }) => (
            <g>
                {placedMarkers.map((p, k) => {
                    const active = hoveredMarker && hoveredMarker._k === k;
                    return (
                        <circle
                            key={k}
                            cx={xScale(p.x)}
                            cy={5}
                            r={active ? 5 : 3}
                            fill={sentimentDotColor(theme, p.sentiment)}
                            stroke={theme.palette.background.paper}
                            strokeWidth={1}
                            style={{ cursor: 'pointer', pointerEvents: 'all' }}
                            onMouseEnter={() => setHoveredMarker({ ...p, _k: k })}
                            onMouseLeave={() => setHoveredMarker(null)}
                        />
                    );
                })}
            </g>
        );
    }, [placedMarkers, theme, hoveredMarker]);

    // Portal tooltip for a hovered event dot, positioned at the cursor.
    const eventTooltip = (hoveredMarker && mouse.x && mouse.y)
        ? createPortal(
            <div style={{
                position: 'fixed',
                left: mouse.x + 12,
                top: mouse.y + 12,
                zIndex: 9999,
                pointerEvents: 'none',
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 6,
                boxShadow: theme.shadows[3],
                padding: '6px 9px',
                fontSize: 11,
                maxWidth: 240,
            }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                    {hoveredMarker.stream || 'Event'}
                </div>
                {hoveredMarker.sentiment && (
                    <div style={{
                        color: sentimentDotColor(theme, hoveredMarker.sentiment),
                        textTransform: 'capitalize',
                    }}>
                        {hoveredMarker.sentiment}
                        {Number.isFinite(Number(hoveredMarker.rank))
                            ? ` · rank ${Math.round(Number(hoveredMarker.rank))}` : ''}
                    </div>
                )}
                {Number.isFinite(Number(hoveredMarker.t)) && (
                    <div style={{ color: theme.palette.text.secondary }}>
                        {new Date(Number(hoveredMarker.t)).toLocaleString(undefined, {
                            year: 'numeric', month: 'short', day: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                        })}
                    </div>
                )}
            </div>,
            document.body,
        )
        : null;

    const minValueSeries = {
        id: 'minValue',
        data: windowed.map((bin, index) => ({
            x: index,
            y: bin.minValue,
            bin: bin
        }))
    };

    const maxValueSeries = {
        id: 'maxValue',
        data: windowed.map((bin, index) => ({
            x: index,
            y: bin.maxValue,
            bin: bin
        })),
    };

    const avgValueSeries = {
        id: 'avgValue',
        data: windowed.map((bin, index) => ({
            x: index,
            y: bin.avgValue,
            bin: bin
        }))
    };

    // Order the series such that avgValue is last (and therefore on top)
    const formattedData = [minValueSeries, maxValueSeries, avgValueSeries];

    const lineColor = series => {
        if (series.id === 'minValue' || series.id === 'maxValue') {
            return theme.palette.mode === 'dark' ? '#66bb6a' : '#4caf50';
        }
        // avgValue line - darker color for visibility
        return theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32';
    };

    const chartTheme = {
        "axis": {
            "ticks": {
                "text": {
                    "fontSize": 9,
                    "fill": theme.palette.text.secondary
                }
            }
        },
        "grid": {
            "line": {
                "stroke": theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                "strokeWidth": 0.7,
            },
        },
        "tooltip": {
            "container": {
                "background": theme.palette.background.paper,
                "color": theme.palette.text.primary,
                "fontSize": 12,
            }
        }
    };

    const gradientColors = theme.palette.mode === 'dark' 
        ? [
            { offset: 0, color: '#2e7d32' },
            { offset: 100, color: 'rgba(46, 125, 50, 0.1)' }
        ]
        : [
            { offset: 0, color: '#4caf50' },
            { offset: 100, color: 'rgba(76, 175, 80, 0.1)' }
        ];

    // The in-chart volume strip (CustomVolumeLayer + the ``volume_*``
    // constants above) was retired in favour of the separate
    // ``DataDensityStrip`` rendered by SignalCard directly below this
    // chart. Two volume strips next to each other duplicated the
    // information and made the chart look like it had bars baked in.
    const customLayers = [
        'grid',
        CustomAreaLayer,
        'markers',
        'axes',
        'areas',
        'lines',
        'points',
        'slices',
        'mesh',
        // After ``mesh`` so the dots sit on top and capture hover (showing
        // the event tooltip instead of the underlying bin's).
        ...(EventMarkersLayer ? [EventMarkersLayer] : []),
        'crosshair',
        'legends',
    ];

    return (
        <div style={{ height }}>
            {eventTooltip}
            <ResponsiveLine
                data={formattedData}
                keys={['minValue', 'maxValue', 'avgValue']}
                margin={{ top: 10, right: 10, bottom: 10, left: unit ? 58 : 44 }}
                axisTop={null}
                axisRight={null}
                axisBottom={null}
                theme={chartTheme}
                enableGridX={true}
                enableGridY={false}
                axisLeft={{
                    orient: 'left',
                    tickSize: 1,
                    tickValues: 4,
                    tickPadding: 6,
                    tickRotation: 0,
                    // The signal's measurement unit as the axis legend
                    // ("$/MWh") — ticks stay compact numbers.
                    legend: unit || undefined,
                    legendOffset: unit ? -50 : 0,
                    legendPosition: 'middle',
                    format: value => {
                       return formatLargeNumber(value)
                    }
                }}
                lineWidth={1}
                colors={lineColor}
                pointSize={0}
                curve="cardinal"
                useMesh={true}
                colorBy={'id'}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
                layers={customLayers} // Using the customLayers array
                tooltip={BinTooltip}
                crosshairType="cross"

                defs={[
                    {
                        id: 'gradient',
                        type: 'linearGradient',
                        colors: gradientColors,
                    },
                ]}
                fill={[{ match: { id: 'maxValue' }, id: 'gradient' }]}
            />
        </div>
    );
};

export default SignalBinMiniChart;